import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { useMessages } from '../hooks/useMessages';
import { useOnlineUsers } from '../hooks/useOnlineUsers';
import Message from './Message';
import MessageInput from './MessageInput';
import OnlineUsers from './OnlineUsers';
import LanguageSelector from './LanguageSelector';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiLogOut } = FiIcons;

const ChatRoom = ({ user, onLeave }) => {
  const [showUserList, setShowUserList] = useState(false);
  const messagesEndRef = useRef(null);
  const { t } = useLanguage();
  const { onlineUsers, updateActivity } = useOnlineUsers(user);
  const { messages, loading, sendMessage, deleteMessage } = useMessages(user, updateActivity);
  const messageContainerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 監聽用戶活動
  useEffect(() => {
    const handleActivity = () => {
      if (updateActivity) {
        updateActivity();
      }
    };

    // 添加各種活動監聽器
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [updateActivity]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">{t('connectingToChat')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Main chat area */}
      <div className="flex-1 flex flex-col">
        {/* Header - 固定在頂部 */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between fixed top-0 left-0 right-0 z-[60] shadow-sm h-[60px]">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-gray-800">Chatroom</h1>
            <span className="text-sm text-gray-500">
              {t('nickname')}: {user.nickname}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <button
              onClick={() => setShowUserList(!showUserList)}
              className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
            >
              <SafeIcon icon={FiUsers} className="w-4 h-4" />
              <span className="hidden sm:inline">{onlineUsers.length}</span>
            </button>
            <button
              onClick={onLeave}
              className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
            >
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
              <span className="hidden sm:inline">{t('leave')}</span>
            </button>
          </div>
        </div>

        {/* Messages container - 調整頂部間距和底部間距 */}
        <div
          ref={messageContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-4 mt-[60px] mb-24"
        >
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <p>{t('noMessages')}</p>
            </div>
          ) : (
            messages.map((message) => (
              <Message
                key={message.id}
                message={message}
                currentUserId={user.id}
                onDelete={deleteMessage}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message input - 固定在底部 */}
        <div className="fixed bottom-0 left-0 right-0 z-[50] bg-white shadow-up">
          <MessageInput onSend={sendMessage} />
        </div>
      </div>

      {/* Online users sidebar - 作為固定的側邊欄 */}
      <OnlineUsers
        users={onlineUsers}
        isOpen={showUserList}
        onClose={() => setShowUserList(false)}
      />
    </div>
  );
};

export default ChatRoom;